import React, { useEffect, useState, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { FileTextIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PercentIcon, GlobeIcon, BuildingIcon, UsersIcon, CalendarIcon, ChevronDownIcon, PencilIcon } from 'lucide-react';
import { BIKPI } from './BIKPI';
import { BICard } from './BICard';
import { BIInsights } from './BIInsights';
import { globalKPIs, buComparisonData, trendData, countryData, dealerData, globalInsights } from '../../data/biData';
const COLORS = ['#3b82f6', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6'];
type DateFilterMode = 'year' | 'quarter' | 'month';
const currentYear = new Date().getFullYear();
const years = Array.from({
  length: 6
}, (_, i) => currentYear - i);
const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function GlobalView() {
  const [dateMode, setDateMode] = useState<DateFilterMode>('year');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedMonth, setSelectedMonth] = useState('January');
  const [isEditingDate, setIsEditingDate] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const dateLabel = dateMode === 'year' ? `${selectedYear}` : dateMode === 'quarter' ? `${selectedQuarter} ${selectedYear}` : `${selectedMonth} ${selectedYear}`;
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsEditingDate(false);
      }
    }
    if (isEditingDate) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditingDate]);
  return <div className="space-y-6" data-id="element-827" data-mp="353mirp3zv5ri">
      {/* Global Identity Header — globe icon + network stats */}
      <div className="flex flex-wrap items-center gap-4" data-id="element-828" data-mp="303i484mncl3x">
        {/* Globe Identity */}
        <div className="flex items-center gap-4 mr-auto" data-id="element-829" data-mp="2w26bhq8h7jri">
          <div className="w-12 h-12 flex items-center justify-center flex-shrink-0" data-id="element-830" data-mp="2w0lgjzi91yt4">
            <img src="https://cdn.magicpatterns.com/uploads/jJzF1PFVpDXeTTbKYPBGHa/earth-globe-with-continents-maps.svg" alt="Global Network" className="w-12 h-12 filter invert opacity-90" data-id="element-831" data-mp="2r2tliwmb5wkp" />
          </div>
          <div data-id="element-832" data-mp="2hc7cjl93k4az">
            <h3 className="text-white font-semibold text-[22px]" data-id="element-833" data-mp="2hbp841ca6jrz">
              Global Quotes Analytics
            </h3>
            <p className="text-blue-300/70 text-sm leading-tight mt-0.5" data-id="element-834" data-mp="28d3sof23vkvf">
              Global analysis of quotes across all business units
            </p>
            {/* Inline date filter */}
            <div className="relative mt-1" ref={datePickerRef} data-id="element-835" data-mp="1yeo4ajugqppm">
              <button onClick={() => setIsEditingDate(!isEditingDate)} className="flex items-center gap-2 text-sm text-blue-300 hover:text-white transition-colors group" data-id="element-836" data-mp="1yffwdkouq5b5">
                <CalendarIcon size={15} className="text-blue-400/70 group-hover:text-blue-300" data-id="element-837" data-mp="iungp2ylu2a2" />
                <span className="font-medium" data-id="element-838" data-mp="nvuov34nt3h9">{dateLabel}</span>
                <PencilIcon size={12} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-400" data-id="element-839" data-mp="rw4rq6x5ndrw" />
              </button>

              {/* Date picker popover */}
              {isEditingDate && <div className="absolute top-full left-0 mt-2 bg-[#0f1d3a] rounded-xl border border-white/10 shadow-2xl p-3 z-50 min-w-[240px]" data-id="element-840" data-mp="4yz0bjrkicn">
                  <div className="space-y-2.5" data-id="element-841" data-mp="45947lzqf4qg">
                    {/* Period type */}
                    <div data-id="element-842" data-mp="8yc0i1vw8sac">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-843" data-mp="8ytwcxen94hq">
                        Period
                      </p>
                      <div className="flex gap-1" data-id="element-844" data-mp="3clu6kjxuyzkl">
                        {(['year', 'quarter', 'month'] as DateFilterMode[]).map(mode => <button key={mode} onClick={() => setDateMode(mode)} className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${dateMode === mode ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-845" data-mp="2tisc26i12nla">
                              {mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>)}
                      </div>
                    </div>

                    {/* Month selector */}
                    {dateMode === 'month' && <div data-id="element-846" data-mp="378lzckdvwzsw">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-847" data-mp="37bi00gh4a2l1">
                          Month
                        </p>
                        <div className="relative" data-id="element-848" data-mp="3h8mncjmp843f">
                          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-849" data-mp="3h5qrr1ybpri5">
                            {months.map(m => <option key={m} value={m} className="bg-slate-800 text-white" data-id="element-850" data-mp="7avvtdv3swcw">
                                {m}
                              </option>)}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-851" data-mp="3tpf9f8c987ib">
                            <ChevronDownIcon size={12} data-id="element-852" data-mp="3foutql1myrt1" />
                          </div>
                        </div>
                      </div>}

                    {/* Quarter selector */}
                    {dateMode === 'quarter' && <div data-id="element-853" data-mp="35ucmx70xag50">
                        <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-854" data-mp="3ar2ypi5p2pkm">
                          Quarter
                        </p>
                        <div className="flex gap-1" data-id="element-855" data-mp="141ocacly8qf7">
                          {quarters.map(q => <button key={q} onClick={() => setSelectedQuarter(q)} className={`flex-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${selectedQuarter === q ? 'bg-blue-600 text-white' : 'bg-white/5 text-blue-300 hover:bg-white/10 hover:text-white'}`} data-id="element-856" data-mp="18yn76xdft1eg">
                              {q}
                            </button>)}
                        </div>
                      </div>}

                    {/* Year selector */}
                    <div data-id="element-857" data-mp="snwdyd9ioawm">
                      <p className="text-[10px] uppercase tracking-wider text-blue-400/60 mb-1" data-id="element-858" data-mp="1q9tsjzkrzw3a">
                        Year
                      </p>
                      <div className="relative" data-id="element-859" data-mp="1lcagn6bjvvei">
                        <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))} className="w-full appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-7 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer" data-id="element-860" data-mp="1gipfj5enulb3">
                          {years.map(y => <option key={y} value={y} className="bg-slate-800 text-white" data-id="element-861" data-mp="247qsn5yfukmp">
                              {y}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1.5 text-blue-300" data-id="element-862" data-mp="2gjt57imrljlq">
                          <ChevronDownIcon size={12} data-id="element-863" data-mp="2lkqltqs4m5l4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* Active BUs Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-864" data-mp="2zgucja3sjvsu">
          <div className="flex items-center gap-3" data-id="element-865" data-mp="34ayjvugyy5ug">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-866" data-mp="396x4d6z6f3pl">
              <GlobeIcon size={18} className="text-blue-200/70" data-id="element-867" data-mp="3e7unbls0g1s5" />
            </div>
            <div data-id="element-868" data-mp="3j5mo75amuy8h">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-869" data-mp="3j77j4w0v0j6v">
                Business Unit
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-870" data-mp="8adz8yc4cidm">
                {globalKPIs.activeBUs}
              </p>
            </div>
          </div>
        </div>

        {/* Active Countries Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-871" data-mp="hblsh1zs2t0u">
          <div className="flex items-center gap-3" data-id="element-872" data-mp="3kt3qyitnunol">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-873" data-mp="3ktn7pw7kr611">
              <GlobeIcon size={18} className="text-blue-200/70" data-id="element-874" data-mp="3gsthgw6ntf4z" />
            </div>
            <div data-id="element-875" data-mp="3ujyo4i8g37ot">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-876" data-mp="3ukhur2sna9nj">
                Active Countries
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-877" data-mp="3pq3vgpav3idl">
                {globalKPIs.activeCountries}
              </p>
            </div>
          </div>
        </div>

        {/* Active Dealers Panel */}
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/10 px-5 py-3" data-id="element-878" data-mp="1clie1sp8g3i9">
          <div className="flex items-center gap-3" data-id="element-879" data-mp="1hcgvivlyj7n3">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-880" data-mp="1hebisgkpbbuc">
              <BuildingIcon size={18} className="text-blue-200/70" data-id="element-881" data-mp="13iplzn887nrd" />
            </div>
            <div data-id="element-882" data-mp="18k56ne6hrs98">
              <p className="text-xs text-blue-300/70 leading-tight" data-id="element-883" data-mp="18hizvgrfdgs2">
                Active Dealers
              </p>
              <p className="text-xl font-bold text-white leading-tight" data-id="element-884" data-mp="20b30ilklhiwa">
                {globalKPIs.activeDealers}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-885" data-mp="1r9v98nghxibp">
        <BIKPI title="Total Quotes" value={globalKPIs.totalQuotes} trend={globalKPIs.variation.total} icon={<FileTextIcon size={18} data-id="element-887" data-mp="fv818bndppxw" />} color="blue" delay={0.1} data-id="element-886" data-mp="aw4veozkrfov" />
        <BIKPI title="Won Quotes" value={globalKPIs.wonQuotes} trend={globalKPIs.variation.won} icon={<CheckCircleIcon size={18} data-id="element-889" data-mp="2s4o584szsvp7" />} color="emerald" delay={0.2} data-id="element-888" data-mp="fwj4macwwvoh" />
        <BIKPI title="Win Rate" value={`${globalKPIs.winRate}%`} trend={globalKPIs.variation.winRate} icon={<PercentIcon size={18} data-id="element-891" data-mp="2if4ksupmsts8" />} color="teal" delay={0.3} data-id="element-890" data-mp="2x3iot8ysaoad" />
        <BIKPI title="Active Quotes" value={globalKPIs.activeQuotes} icon={<ClockIcon size={18} data-id="element-893" data-mp="2nbv2tnkzxzga" />} color="amber" delay={0.4} data-id="element-892" data-mp="2icicutytwc9h" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-894" data-mp="29c3jd7bpnquw">
        <BICard title="Quotes Trend (YTD)" className="lg:col-span-2" data-id="element-895" data-mp="2dfi8qoljhjl0">
          <div className="h-80" data-id="element-896" data-mp="1zfgy4hfh2xsc">
            <ResponsiveContainer width="100%" height="100%" data-id="element-897" data-mp="1zga3pf2p3h8w">
              <LineChart data={trendData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-898" data-mp="24ek3y43a181d">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-899" data-mp="3tu1cydh43z3t" />
                <XAxis dataKey="month" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-900" data-mp="2lvtpas7tkc3" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-901" data-mp="1n5r7et00ytbn" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} data-id="element-902" data-mp="1i7hb2f2kdmog" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-903" data-mp="1s317d2l1indq" />
                <Line type="monotone" dataKey="total" name="Total Quotes" stroke="#3b82f6" strokeWidth={3} dot={{
                r: 4,
                fill: '#3b82f6',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} activeDot={{
                r: 6
              }} data-id="element-904" data-mp="25xk00mb6mciu" />
                <Line type="monotone" dataKey="won" name="Won Quotes" stroke="#10b981" strokeWidth={3} dot={{
                r: 4,
                fill: '#10b981',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} data-id="element-905" data-mp="lfvz5bi7mpjb" />
                <Line type="monotone" dataKey="lost" name="Lost Quotes" stroke="#ef4444" strokeWidth={3} dot={{
                r: 4,
                fill: '#ef4444',
                strokeWidth: 2,
                stroke: '#0f1d3a'
              }} data-id="element-906" data-mp="1669kf8c8e7pq" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Quotes by Business Unit" data-id="element-907" data-mp="1k2d3c5hnmtmw">
          <div className="h-72" data-id="element-908" data-mp="1p2r7hcg4xjfn">
            <ResponsiveContainer width="100%" height="100%" data-id="element-909" data-mp="1p1pwt9316yyg">
              <BarChart data={buComparisonData} margin={{
              top: 20,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-910" data-mp="1t4ckcxso5xpu">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-911" data-mp="22uxn8xfdloek" />
                <XAxis dataKey="name" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-912" data-mp="27uk3lfqi3krl" />
                <YAxis stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-913" data-mp="2ct52nbu0nyeg" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} itemStyle={{
                color: '#fff'
              }} data-id="element-914" data-mp="161mq7sxvhqc" />
                <Legend wrapperStyle={{
                fontSize: '12px',
                color: '#93c5fd'
              }} data-id="element-915" data-mp="3nfc9x6uuybg0" />
                <Bar dataKey="won" name="Won" stackId="a" fill="#10b981" radius={[0, 0, 4, 4]} data-id="element-916" data-mp="jwj97un6hh39" />
                <Bar dataKey="active" name="Active" stackId="a" fill="#3b82f6" data-id="element-917" data-mp="exg3e7zdj6u8" />
                <Bar dataKey="lost" name="Lost" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} data-id="element-918" data-mp="ezj2rimf2cbm" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BICard>

        <BICard title="Win Rate by Business Unit" data-id="element-919" data-mp="57d4xs9hh0oi">
          <div className="h-72" data-id="element-920" data-mp="12nahesvfj1g2">
            <ResponsiveContainer width="100%" height="100%" data-id="element-921" data-mp="12olium1ejx40">
              <BarChart data={buComparisonData} layout="vertical" margin={{
              top: 20,
              right: 30,
              left: 40,
              bottom: 5
            }} data-id="element-922" data-mp="xvsj6cksjeys">
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" horizontal={false} data-id="element-923" data-mp="nd3yxd8px8a8" />
                <XAxis type="number" domain={[0, 100]} stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-924" data-mp="8gcz52v4tuk3" />
                <YAxis dataKey="name" type="category" stroke="#93c5fd" fontSize={12} tickLine={false} axisLine={false} data-id="element-925" data-mp="deek5tc4c3y7" />
                <Tooltip contentStyle={{
                backgroundColor: '#0f1d3a',
                borderColor: '#ffffff20',
                borderRadius: '8px',
                color: '#fff'
              }} formatter={value => [`${value}%`, 'Win Rate']} data-id="element-926" data-mp="1163f7hgbr6iu" />
                <Bar dataKey="winRate" name="Win Rate" fill="#0ea5e9" radius={[0, 4, 4, 0]} barSize={20} data-id="element-927" data-mp="wdsdqa5xrco6">
                  {buComparisonData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.winRate > 75 ? '#10b981' : entry.winRate < 70 ? '#ef4444' : '#0ea5e9'} data-id="element-928" data-mp="1k23dtb3t58be" data-mpi={index} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BICard>
      </div>

      {/* Tables & Insights Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-929" data-mp="34l5r0a394o19">
        <div className="lg:col-span-2 space-y-6" data-id="element-930" data-mp="3jb35tzfak1ds">
          <BICard title="Business Units Performance" data-id="element-931" data-mp="3j9s2g0prcvn7">
            <div className="overflow-x-auto" data-id="element-932" data-mp="3eb8fpy3cbfts">
              <table className="w-full text-sm text-left" data-id="element-933" data-mp="3edl8z7ejtcm5">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-934" data-mp="2qmz0xg4jnsc5">
                  <tr data-id="element-935" data-mp="2lnup0hq8jgi4">
                    <th className="px-4 py-3 font-medium" data-id="element-936" data-mp="2lonqp4ac7fte">Business Unit</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-937" data-mp="2zndneit8vdh8">Total</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-938" data-mp="2vkr3d42tewul">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-939" data-mp="ewd9gja1j83r">Active</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-940" data-mp="eyg2zdaejjd8">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-941" data-mp="33oqhwh10fo79">
                  {buComparisonData.map((bu, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-942" data-mp="38ntnc2kwjwfr" data-mpi={idx}>
                      <td className="px-4 py-3 font-medium text-white" data-id="element-943" data-mp="3dn69sm7v31ht">
                        {bu.name}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-100" data-id="element-944" data-mp="3hohs06oeokfz">
                        {bu.total}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-400" data-id="element-945" data-mp="2pxv4crfkgixc">
                        {bu.won}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-400" data-id="element-946" data-mp="2tz70lfsy42ei">
                        {bu.active}
                      </td>
                      <td className="px-4 py-3 text-right text-white" data-id="element-947" data-mp="2yrgkof7dq88v">
                        <span className={`px-2 py-1 rounded-full text-xs ${bu.winRate > 75 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-blue-500/20 text-blue-400'}`} data-id="element-948" data-mp="227hmftqp40gh">
                          {bu.winRate}%
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>

          <BICard title="Top Countries" data-id="element-949" data-mp="1ppf14cijwitd">
            <div className="overflow-x-auto" data-id="element-950" data-mp="1po3tg1lziqwz">
              <table className="w-full text-sm text-left" data-id="element-951" data-mp="1kqu8oz9jj3fh">
                <thead className="text-xs text-blue-300 uppercase bg-white/5 border-b border-white/10" data-id="element-952" data-mp="1kqap7e48s6zc">
                  <tr data-id="element-953" data-mp="1fssf3kehnlpr">
                    <th className="px-4 py-3 font-medium" data-id="element-954" data-mp="1b0r5654sm72w">Country</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-955" data-mp="1azg1e5bckzbs">Total</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-956" data-mp="162h9ltf5p4gr">Won</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-957" data-mp="120voxlwdrlw0">
                      Win Rate
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-958" data-mp="s1wq0v1f34m7">
                  {countryData.slice(0, 5).map((country, idx) => <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-959" data-mp="s2oku3n6wybf" data-mpi={idx}>
                      <td className="px-4 py-3 font-medium text-white" data-id="element-960" data-mp="2fwi1hub8dyxi">
                        {country.name}
                      </td>
                      <td className="px-4 py-3 text-right text-blue-100" data-id="element-961" data-mp="26yfumy4tgc2b">
                        {country.total}
                      </td>
                      <td className="px-4 py-3 text-right text-emerald-400" data-id="element-962" data-mp="2bvg2nel87ow2">
                        {country.won}
                      </td>
                      <td className="px-4 py-3 text-right text-white" data-id="element-963" data-mp="21ytkwyo75dy9">
                        {country.winRate}%
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </BICard>
        </div>

        <div className="lg:col-span-1" data-id="element-964" data-mp="1s9jmz26o9128">
          <BIInsights insights={globalInsights} data-id="element-965" data-mp="1e9916dk7ycnx" />
        </div>
      </div>
    </div>;
}